import { useContext, useMemo } from 'react';
import { AuthContext } from '../../providers';

interface TenantInfo {
  tenantType?: string;
  tenantId?: string;
  tenantAlias?: string;
  tenantIdentifier?: string;
  humioUrlPrefix: string;
  isPOVTenant: boolean;
  isPrimeTenant: boolean;
  isStandardTenant: boolean;
  repoSlug?: string;
  isLoading: boolean;
}

/**
 * Hook to retrieve and compute tenant information from the authenticated user
 * Determines tenant type, Humio URL prefix, and other tenant-specific configurations
 *
 * @returns {TenantInfo} Computed tenant information including dynamic Humio URL prefix
 */
export const useTenantInfo = (): TenantInfo => {
  const { user, userLicense, userLicenseLoading } = useContext(AuthContext);

  return useMemo(() => {
    const tenantId = user?.attributes?.['custom:tenant_id'];
    const tenantAlias = user?.attributes?.['custom:alias'];

    // If license is loading or no tenant_type, return loading state
    if (userLicenseLoading || !userLicense?.tenant_type) {
      return {
        tenantType: undefined,
        tenantId: undefined,
        tenantAlias: undefined,
        tenantIdentifier: undefined,
        humioUrlPrefix: 'search',
        isPOVTenant: false,
        isPrimeTenant: false,
        isStandardTenant: false,
        repoSlug: undefined,
        isLoading: true,
      };
    }

    // License is loaded and has tenant_type
    const tenantType = userLicense.tenant_type.toUpperCase();

    /**
     * Determine Humio URL prefix
     * Priority:
     * 1. Use systemInfo.humio_root from backend (dynamic configuration)
     * 2. Fall back to legacy hardcoded logic based on tenant type
     *
     * This dual approach ensures backward compatibility while supporting
     * the new dynamic configuration from ptrx_tenants.systemInfo
     */
    let humioUrlPrefix = 'search'; // default fallback for standard tenants

    // Check for new system_info field (added in backend)
    const systemInfoHumioRoot = userLicense.system_info?.humio_root;

    if (systemInfoHumioRoot) {
      // Use the dynamic configuration from backend
      humioUrlPrefix = systemInfoHumioRoot;
    } else {
      // Fallback to legacy logic for backward compatibility
      // This can be removed once all tenants have systemInfo.humio_root populated
      if (tenantType === 'PRIME' && tenantId) {
        humioUrlPrefix = `search-${tenantId}`;
      } else if (tenantType === 'POV') {
        humioUrlPrefix = 'search-internal';
      }
      // Standard tenants use default 'search' (already set above)
    }

    // Determine tenant type booleans
    const isPOVTenant = tenantType === 'POV';
    const isPrimeTenant = tenantType === 'PRIME';
    const isStandardTenant = !isPOVTenant && !isPrimeTenant;

    // All tenant types use tenantId for the repository identifier
    const tenantIdentifier = tenantId;

    // Build repo slug for Humio/LogScale paths
    // Format: {tenantId}-logs
    const repoSlug = tenantIdentifier ? `${tenantIdentifier}-logs` : undefined;

    return {
      tenantType,
      tenantId,
      tenantAlias,
      tenantIdentifier,
      humioUrlPrefix,
      isPOVTenant,
      isPrimeTenant,
      isStandardTenant,
      repoSlug,
      isLoading: false,
    };
  }, [
    user?.attributes,
    userLicenseLoading,
    userLicense?.tenant_type,
    userLicense?.system_info?.humio_root,
  ]);
};
